import { MouseEvent, useCallback } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ImageTransformOptions, ImageTransformType } from '@diritto/common/imageKit/constants';
import { ContentPrivateRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { ImageRatio } from '@diritto/common/base/constants';
import { isPC } from 'constants/layout';
import { ModalType } from 'constants/modal';
import { getCoverImageBorder, getCoverImageStyle } from 'styles/common';
import { IkImage } from 'ui-components/Imagekit/IkImage';
import { ModalHandler } from 'utils/ModalHandler';
import { ZIndex } from 'constants/zIndex';
import { BlockIcon } from 'icons/BlockIcon';
import { Dim } from 'ui-components/common/Dim';

interface ContentCoverPrivateProps {
  className?: string;
  content: ContentPrivateRelayDto;
  canEnlarge?: boolean;
  size?: number;
  pcSize?: number;
  imageSize?: number;
  ratio?: number;
}

export const ContentPrivateCover = ({
  className,
  content,
  canEnlarge = false,
  size = 100,
  pcSize,
  imageSize,
  ratio = ImageRatio,
}: ContentCoverPrivateProps) => {
  const handleClickCover = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!canEnlarge) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      ModalHandler.show(ModalType.ContentCover, { imageFile: content.cover });
    },
    [content.cover, canEnlarge],
  );

  return (
    <StyledCoverWrapper
      className={className}
      canEnlarge={canEnlarge}
      ratio={ratio}
      size={size}
      pcSize={pcSize ?? size}
      onClick={handleClickCover}>
      <IkImage
        transform={{
          path: content.cover.path,
          isPrivate: content.cover.isPrivate,
          transformation: imageSize
            ? { ...ImageTransformOptions[ImageTransformType.ContentCover], width: imageSize }
            : ImageTransformOptions[ImageTransformType.ContentCover],
        }}
        alt={`${content.title} 표지`}
      />
      <Dim />
      <div className="absolute inset-0 flex items-center justify-center">
        <StyledBlockIcon size={size} color={Color.Gray100} />
      </div>
    </StyledCoverWrapper>
  );
};

const StyledCoverWrapper = styled.div(
  ({ canEnlarge, size, pcSize, ratio }: { size: number; pcSize: number; canEnlarge: boolean; ratio: number }) => css`
    ${getCoverImageBorder()};
    position: relative;
    z-index: ${ZIndex.ForStackingContext};
    width: ${size}px;
    height: ${size * ratio}px;
    background-color: ${Color.White};

    cursor: ${canEnlarge ? 'pointer' : 'inherit'};

    & img {
      ${getCoverImageStyle()}
    }

    @media (${isPC}) {
      width: ${pcSize}px;
      height: ${pcSize * ratio}px;
    }
  `,
);

const StyledBlockIcon = styled(BlockIcon)<{ size: number }>`
  opacity: 0.4;
  width: ${({ size }) => Math.round(size * 0.667)}px;
  height: ${({ size }) => Math.round(size * 0.667)}px;
`;
